import React, { useState } from 'react';
import { Table, Alert, Snackbar, CircularProgress, Box, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, useTheme, Button, Select, MenuItem, TextField, FormControl, InputLabel, Typography } from '@mui/material';

const Model = ({ modelInputData, setVisualizationData,toggleSidebar }) => {
  const theme = useTheme();
  // State for selected option and any response we might get from the backend
  const [selectedOption, setSelectedOption] = useState('');
  const [response, setResponse] = useState(null);
  const [columnToPredict, setColumnToPredict] = useState('');
  const [inputValues, setInputValues] = useState({});
  const [loading, setLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  // List of options for the dropdown (modify as needed)
  const regressionModels = [
    "Linear - Linear Regression",
    "Linear - Ridge Regression",
    "Linear - Lasso",
    "Linear - Elastic Net",
    "Linear - SGD Regressor",
    "Support Vector Machines - SVR",
    "Nearest Neighbors - KNeighbors Regressor",
    "Tree-based - Decision Tree Regressor",
    "Tree-based - ExtraTrees Regressor",
    "Ensemble Methods - Random Forest Regressor",
    "Ensemble Methods - Gradient Boosting Regressor",
    "Ensemble Methods - AdaBoost Regressor",
    "Ensemble Methods - Bagging Regressor",
    "Neural Network - MLP Regressor",
    "Other - Huber Regressor",
    "Other - PassiveAggressive Regressor"
  ];

  // Handler for when the dropdown value changes
  const handleSelectChange = (e) => {
    setSelectedOption(e.target.value);
  };

  // Handler for when the 'Train' button is clicked
  const handleButtonClick = async () => {
    if (!selectedOption) {
      alert('Please select an option before training');
      return;
    }

    setLoading(true);
    // Create a copy of inputValues and remove the columnToPredict if it exists
    const adjustedInputValues = { ...inputValues };
    delete adjustedInputValues[columnToPredict];

    try {
      console.log({ option: selectedOption, input_data: modelInputData, target_column: columnToPredict });
      const result = await fetch(`${process.env.REACT_APP_BACK_END_URL}/workflow/train_data`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          option: selectedOption,
          input_data: modelInputData,
          target_column: columnToPredict,
          input_values: adjustedInputValues
        }),
      });

      const data = await result.json();
      
      setResponse(data);
      setVisualizationData(data.prediction_data)
      setSnackbar({ open: true, message: 'Training & Prediction Successful', severity: 'success' });
      setSnackbarOpen(true)
    } catch (error) {
      console.error('Error sending data to backend:', error);
      setSnackbar({ open: true, message: 'Error Occured While Training the Model', severity: 'error' });
      setSnackbarOpen(true)
    } finally {
      // Set loading to false regardless of the outcome
      setLoading(false);
      setTimeout(() => {
        toggleSidebar();
      }, 6000);
    }
  };
  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  const handleColumnChange = (e) => {
    setColumnToPredict(e.target.value);
  };

  const handleInputChange = (e, rowValue) => {
    setInputValues(prev => ({ ...prev, [rowValue]: e.target.value }));
  };

  return (
    <>
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', m: 2 }}>
          <CircularProgress style={{ color: 'white' }} />
        </Box>
      )}
      <Typography variant="h5" style={{ color: 'white' }}>
        Model
      </Typography>
      <Paper elevation={3} sx={{
        marginTop: '15px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
        backgroundColor: theme.palette.grey[400],
        padding: '10px'
      }}>
        <FormControl variant="outlined" fullWidth>
          <InputLabel id="select-label" style={{ color: 'black' }}>Select a Model</InputLabel>
          <Select
            labelId="select-label"
            value={selectedOption}
            onChange={handleSelectChange}
            label="Select an option"
            renderValue={(selected) => (
              <Box component="span" sx={{ color: 'black' }}>
                {selected}
              </Box>
            )}
            sx={{
              // This will change the text color of the selected item
              backgroundColor: 'black', // change 'desiredColor' to your preferred color
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'black', // If you want to change the border color
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'black', // Border color on hover
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: 'black', // Border color when focused
              },
              // Add this block to target the dropdown icon
              '& .MuiSvgIcon-root': {
                color: 'white', // This changes the dropdown icon color
              }
            }}
          >
            {regressionModels.map((option, index) => (
              <MenuItem style={{ color: theme.palette.black[100], backgroundColor: 'menuItemColor' }} key={index} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>

        </FormControl>
        <TextField
          label="Column to be predicted"
          variant="outlined"
          fullWidth
          value={columnToPredict}
          onChange={handleColumnChange}
          sx={{
            '& .MuiOutlinedInput-input': {
              color: theme.palette.black[100],
            },
            '& .MuiOutlinedInput-root': {
              backgroundColor: theme.palette.white[100],
            },
            marginTop: '10px'
          }}
        />

        {columnToPredict && (
          <TableContainer component={Paper} style={{ marginTop: '20px' }}>
            <Table >
              <TableHead>
                <TableRow>
                  <TableCell style={{ color: theme.palette.black[100] }}>Row Value</TableCell>
                  <TableCell style={{ color: theme.palette.black[100] }}>User Input</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {modelInputData.values[0].map((column, index) => {
                  if (column === columnToPredict) return null; // Skip rendering the row for the column to be predicted

                  return (
                    <TableRow key={index}>
                      <TableCell style={{ color: theme.palette.black[100] }}>{column}</TableCell>
                      <TableCell>
                        <TextField
                          value={inputValues[column] || ''}
                          onChange={e => handleInputChange(e, column)}
                          inputProps={{ style: { color: 'black' } }}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Button
          variant="contained"
          color="primary"
          style={{ marginTop: '20px' }}
          onClick={handleButtonClick}
        >
          Train & predict
        </Button>

        {response && (
          <TableContainer component={Paper} style={{ marginTop: '20px' }}>
            <Table >
              <TableHead>
                <TableRow>
                  <TableCell style={{ color: theme.palette.black[100] }}>Prediction</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell style={{ color: theme.palette.black[100] }}>
                    {columnToPredict}
                  </TableCell>
                  <TableCell style={{ color: theme.palette.black[100] }}>
                    {JSON.stringify(response.prediction_data.prediction)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}

      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>

      </Snackbar>
    </>
  );

}

export default Model;
